import {
  BN,
  isNormalPositive,
  useWallet,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import styled from 'styled-components';
import ButtonWithArrow from '@/components/ButtonWithArrow';
import { GlassPanel } from '@/components/atom/GlassPanel';
import { StyledText } from '@/components/atom/Text';
import { MEDIA_QUERY_DASHBOARD_BREAKPOINT } from '@/configs/mediaQuery';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import { formatRate } from '@/libs/formatNumber';
import { dashboardCardVariant } from '@/styles/motion';
import Skeleton from '../atom/Skeleton';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../atom/Tooltip';

export interface Props {
  depositAmountValue?: string;
  willEarnAmountValue?: string;
  isLoading?: boolean;
}

const Container = styled(GlassPanel)`
  @media (${MEDIA_QUERY_DASHBOARD_BREAKPOINT}) {
    width: 100%;
    height: 304px;
  }
`;

const GridContainer = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template:
    'a a'
    'b c';
  grid-template-columns: 1fr 1fr;

  ${(p) => p.theme.gap.xl};
  & > *:nth-child(1) {
    grid-area: a;
  }
  & > *:nth-child(2) {
    grid-area: b;
  }
  & > *:nth-child(3) {
    grid-area: c;
  }

  @media (max-width: 1330px) {
    width: 100%;
    grid-template: 'a b c';
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const CenterText = styled(StyledText)`
  /* width: 504px; */
`;

export default function EarnOverviewPanel({
  depositAmountValue,
  willEarnAmountValue,
  isLoading,
}: Props) {
  // translation
  const { t } = useTranslation('dashboard');

  // wallet
  const { account } = useWallet();

  // currency
  const { getNumberWithCurrencyReplace } = useGetCurrencyString();

  // memo
  const apr = useMemo(
    () =>
      new BN(
        isNormalPositive(willEarnAmountValue, true)
          ? willEarnAmountValue ?? 0
          : 0
      )
        .div(
          isNormalPositive(depositAmountValue, true)
            ? depositAmountValue ?? 0
            : 0
        )
        .multipliedBy(100),
    [depositAmountValue, willEarnAmountValue]
  );

  return account ? (
    <GridContainer>
      <GlassPanel
        as={motion.div}
        variants={dashboardCardVariant}
        $flex
        $direction="column"
        $gap="lg"
        $padding="xl"
        $rounded="xxl"
      >
        <StyledText $fontSize="md" $fontWeight="medium" $textColor="textGray">
          {t('totalDeposit')}
        </StyledText>
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            <StyledText $fontSize="xxxl" $fontWeight="bold">
              {getNumberWithCurrencyReplace(depositAmountValue)}
            </StyledText>
          </>
        )}
      </GlassPanel>
      <GlassPanel
        as={motion.div}
        variants={dashboardCardVariant}
        $flex
        $direction="column"
        $gap="lg"
        $padding="xl"
        $rounded="xxl"
      >
        <StyledText
          $fontSize="md"
          $fontWeight="medium"
          $textColor="textGray"
          $whiteSpace="pre"
        >
          {t('receivedReward')}
        </StyledText>
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            <StyledText $fontSize="xxxl" $fontWeight="bold" $whiteSpace="pre">
              {getNumberWithCurrencyReplace(willEarnAmountValue)}
            </StyledText>
          </>
        )}
      </GlassPanel>
      <GlassPanel
        as={motion.div}
        variants={dashboardCardVariant}
        $flex
        $direction="column"
        $gap="lg"
        $padding="xl"
        $rounded="xxl"
        style={{
          flexShrink: 0,
        }}
      >
        <StyledText
          $fontSize="md"
          $fontWeight="medium"
          $textColor="textGray"
          $whiteSpace="pre"
        >
          {t('currentProfitRate')}
        </StyledText>
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            <StyledText
              $fontSize="xxxl"
              $fontWeight="bold"
              $textColor="green"
              $whiteSpace="pre"
            >
              {formatRate(isNormalPositive(apr, true) ? apr : 0)}%
            </StyledText>
          </>
        )}
      </GlassPanel>
    </GridContainer>
  ) : (
    <Container
      as={motion.div}
      variants={dashboardCardVariant}
      $flex
      $direction="column"
      $gap="xl"
      $padding="xl"
      $justify="center"
      $align="center"
      $rounded="xl"
    >
      <CenterText
        $fontSize="xl"
        $fontWeight="bold"
        $whiteSpace="pre-line"
        $textAlign="center"
      >
        {t('depositNow')}
      </CenterText>
      <Tooltip placement="top" offset={16}>
        <TooltipTrigger asChild>
          <ButtonWithArrow
            rounded="lg"
            padding={{
              x: 'xl',
              y: 'md',
            }}
            variant="primary"
            disabled={!account}
          >
            {t('goToDeposit')}
          </ButtonWithArrow>
        </TooltipTrigger>
        <TooltipContent>
          <TooltipArrow />
          <StyledText $fontSize="md" $fontWeight="bold">
            {t('connectWalletFirst')}
          </StyledText>
        </TooltipContent>
      </Tooltip>
    </Container>
  );
}
